'use client'

import * as React from 'react'

import { Button, type ButtonProps } from '@/components/ui/button'
import { PlanLockTooltip, PlanStar } from '@/features/billing/components/plan-lock-tooltip'
import { cn } from '@/lib/utils'

export interface PlanGatedButtonProps extends ButtonProps {
  /** Resolved gating decision — ask `useCapabilities` in the caller. */
  locked: boolean
  /** Feature name shown in the upsell tooltip ("<feature> is available on Pro"). */
  feature: string
  /** Plan that unlocks the feature, e.g. from `useUpgradeHint`. Defaults to Pro. */
  plan?: string
}

/**
 * A button for a plan-gated action. Unlocked, it is a plain `Button`. Locked, it
 * stays visible but disabled, carries a gold star, and explains on hover which
 * plan unlocks it — locked features are shown, never hidden, so users see what
 * upgrading gets them.
 */
export const PlanGatedButton = React.forwardRef<HTMLButtonElement, PlanGatedButtonProps>(
  ({ locked, feature, plan, className, children, ...props }, ref) => {
    if (!locked) {
      return (
        <Button ref={ref} className={className} {...props}>
          {children}
        </Button>
      )
    }

    // `asChild` is dropped since the child must stay inert.
    const { asChild: _asChild, onClick: _onClick, ...buttonProps } = props

    return (
      <PlanLockTooltip locked feature={feature} plan={plan} className="inline-flex">
        <Button
          ref={ref}
          {...buttonProps}
          disabled
          aria-disabled
          className={cn('pointer-events-none gap-1.5', className)}
        >
          {children}
          <PlanStar />
        </Button>
      </PlanLockTooltip>
    )
  }
)
PlanGatedButton.displayName = 'PlanGatedButton'
